// 解码页面

import React, { useState, useEffect, useRef } from 'react';
import { Button, Result, message, Form, Select, Space, Radio, InputNumber } from 'antd';

import { getHeaderList, getColumnDataByHeader } from './utils/excelParser';

const DecodePage = ({ handleCurrentStepMinus, handleCurrentStepAdd, file, setDecodeResult }) => {
    const [form] = Form.useForm();
    // 解析成功状态
    const [decodeSuccess, setDecodeSuccess] = useState(true);
    // 表头
    const [headers, setHeaders] = useState([]);
    // 自定义的间隔时间
    const customIntervalRef = useRef(1);
    // 监听时间间隔变化
    const timeIntervalValue = Form.useWatch('time_interval', form);

    // 解析文件
    useEffect(() => {
        handleFileUpload(file);
    }, [file]);

    // 解析文件
    const handleFileUpload = async (file) => {
        try {
            // 1. 获取表头（已清洗）
            const headerList = await getHeaderList(file);
            setHeaders(headerList); // 👈 这里就是干净的 ["入库时间", "二次供水温度CR25", ...]
            setDecodeSuccess(true);
        } catch (err) {
            setDecodeSuccess(false);
            message.error(err.message);
        }
    };

    // 解析列
    const handleDecodeColumns = async ({ x_label, y1_label, y2_label }) => {
        // 🚨 关键：用户传入的标签可能带引号，必须清洗！
        const cleanHeader = (str) => str?.trim()?.replace(/^['"]+|['"]+$/g, '') || '';
        const cleanedXLabel = cleanHeader(x_label);
        const cleanedY1Label = cleanHeader(y1_label);
        const cleanedY2Label = cleanHeader(y2_label);

        console.log('查询标签（清洗后）:', { cleanedXLabel, cleanedY1Label, cleanedY2Label });

        // ✅ 使用清洗后的标签进行比对（和 getHeaderList 保持一致）
        if (!headers.includes(cleanedXLabel) ||
            !headers.includes(cleanedY1Label) ||
            !headers.includes(cleanedY2Label)) {
            const missing = [];
            if (!headers.includes(cleanedXLabel)) missing.push(cleanedXLabel);
            if (!headers.includes(cleanedY1Label)) missing.push(cleanedY1Label);
            if (!headers.includes(cleanedY2Label)) missing.push(cleanedY2Label);
            message.error(`请确保文件包含指定表头：${missing.join(', ')}`);
            return;
        }

        // ✅ 传入清洗后的标签 —— 和 getColumnDataByHeader 内部清洗规则一致
        const data = await getColumnDataByHeader(file, cleanedXLabel, cleanedY1Label, cleanedY2Label);
        return data;
    };

    // 提交表单
    const onFinish = async (values) => {
        console.log('表单值:', values);
        const x_label = values.x_label;
        const y1_label = values.y1_label;
        const y2_label = values.y2_label;
        const time_interval = values.time_interval === 'custom' ? customIntervalRef.current : Number(values.time_interval);
        const columns_data = await handleDecodeColumns({ x_label, y1_label, y2_label });
        setDecodeResult({
            x_label: x_label,
            y1_label: y1_label,
            y2_label: y2_label,
            x_data: columns_data.col1,
            y1_data: columns_data.col2,
            y2_data: columns_data.col3,
            time_interval: time_interval,
        });
        handleCurrentStepAdd();
    };

    return (
        <div style={{ width: '100%' }}>
            {decodeSuccess === false && <Result
                status="error"
                title="解析失败"
                subTitle="文件解析失败，请检查文件格式是否正确，或尝试重新上传。"
                extra={[
                    <Button type="primary" key="console" onClick={handleCurrentStepMinus}>
                        返回上一步
                    </Button>,
                ]}
            />}
            {decodeSuccess === true && <Form layout='vertical' onFinish={onFinish} form={form}>
                <Form.Item label='X轴标签' name="x_label" rules={[{ required: true, message: '请选择X轴标签' }]}
                    extra="建议选择时间类标签，例如：时间、采集时间等">
                    <Select showSearch placeholder="请选择X轴标签" optionFilterProp="label"
                        options={headers.map((header) => ({ value: header, label: header }))} />
                </Form.Item>
                <Form.Item label='Y轴曲线1标签' name="y1_label" rules={[{ required: true, message: '请选择曲线1标签' }]}
                    extra="建议选择供水温度(℃)，或其他温度类标签。该数值将用于拓扑等效计算的t1值">
                    <Select showSearch placeholder="请选择曲线1标签" optionFilterProp="label"
                        options={headers.map((header) => ({ value: header, label: header }))} />
                </Form.Item>
                <Form.Item label='Y轴曲线2标签' name="y2_label" rules={[{ required: true, message: '请选择曲线2标签' }]}
                    extra="建议选择回水温度(℃)，或其他温度类标签。该数值将用于拓扑等效计算的t2值">
                    <Select showSearch placeholder="请选择曲线2标签" optionFilterProp="label"
                        options={headers.map((header) => ({ value: header, label: header }))} />
                </Form.Item>
                <Form.Item label='数据时间间隔' name="time_interval" rules={[{ required: true, message: '请选择数据时间间隔' }]}
                    required tooltip="采集数据的时间间隔(分钟)">
                    <Radio.Group options={[
                        { label: '5分钟', value: 5 },
                        { label: '10分钟', value: 10 },
                        { label: '30分钟', value: 30 },
                        { label: '60分钟', value: 60 },
                        {
                            label: (<>
                                自定义
                                {
                                    timeIntervalValue === 'custom' && <InputNumber
                                        variant="filled" suffix="分钟" min={1} defaultValue={1} step={1} precision={0}
                                        placeholder="输入数字"
                                        style={{ width: 120, marginInlineStart: 12 }}
                                        onChange={(value) => { customIntervalRef.current = Math.floor(Number(value)) }}
                                    />}
                            </>), value: 'custom'
                        },
                    ]} />
                </Form.Item>
                <Form.Item label={null}>
                    <Space size='large'>
                        <Button color="default" variant="outlined" size='large' onClick={handleCurrentStepMinus}>
                            返回上一步
                        </Button>
                        <Button type="primary" htmlType="submit" size='large'>
                            确定
                        </Button>
                    </Space>
                </Form.Item>
            </Form>}
        </div>
    );
};

export default DecodePage;